import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { addBreadcrumb, setTransactionName } from '../utils/sentry';

export const useSentryTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track route changes
    const routeName = location.pathname;
    setTransactionName(`Route: ${routeName}`);
    
    // Add breadcrumb for navigation
    addBreadcrumb(
      `Navigated to ${routeName}`,
      'navigation',
      'info',
      {
        from: document.referrer,
        to: window.location.href,
        ...location.state,
      }
    );

    // Track page load performance
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      addBreadcrumb(
        'Page Load Performance',
        'performance',
        'info',
        {
          loadTime: navigationEntry.loadEventEnd - navigationEntry.startTime,
          domContentLoaded: navigationEntry.domContentLoadedEventEnd - navigationEntry.startTime,
          firstContentfulPaint: performance.getEntriesByType('paint').find(
            (entry) => entry.name === 'first-contentful-paint'
          )?.startTime,
        }
      );
    }

    // Track resource loading
    const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const resourceStats = resourceEntries.reduce((acc, entry) => {
      const type = entry.initiatorType;
      if (!acc[type]) {
        acc[type] = {
          count: 0,
          totalSize: 0,
          totalDuration: 0,
        };
      }
      acc[type].count++;
      acc[type].totalSize += entry.transferSize || 0;
      acc[type].totalDuration += entry.duration;
      return acc;
    }, {} as Record<string, { count: number; totalSize: number; totalDuration: number }>);

    addBreadcrumb(
      'Resource Loading Stats',
      'performance',
      'info',
      resourceStats
    );

    // Clear performance entries to prevent memory growth
    performance.clearResourceTimings();
  }, [location]);
}; 